// Redact credentials that ride in an API path, before that path is
// reported to Sentry.
//
// Several endpoints carry a SECRET as a URL path segment rather than in a
// header or a body: the customer's booking magic token, the post-service
// pay token (#3063 — it mints a Stripe Checkout Session against the
// operator's connected account), invitee and time-poll tokens, the GDPR
// confirmation tokens, the operator invitation token.
//
// apiClient reports the request path to Sentry on its network-failure and
// 5xx branches. The `beforeSend` hook in main.ts scrubs by KEY NAME
// (/password|totp|token|csrf|secret/i over breadcrumb data keys, plus
// cookie/authorization request headers) — it never looks at `extra.apiPath`
// or the `api.path` tag, and those key names wouldn't match the pattern
// anyway. So the raw token shipped. Redacting here means the value never
// enters the event and no downstream scrubber has to know these shapes.
//
// Rules are anchored regexes rather than a bare prefix list because the
// credential is not always the last segment
// (`/api/bookings/{token}/confirm`) and is not always the first segment
// after the prefix (`/api/public/action-items/{id}/done/{token}`). Group 1
// is the part that survives; whatever follows the matched segment is kept
// verbatim, so the endpoint stays identifiable in Sentry — the point is to
// lose the secret, not the signal.
//
// Adding a token-bearing endpoint? Add its shape here.
const TOKEN_PATH_RULES: readonly RegExp[] = [
  // Post-service pay link.
  /^(\/api\/betal\/)[^/?#]+/,
  // Customer booking magic token — bare, and every sub-resource
  // (/confirm, /series, /checkout, /documents, /invitees, /propose-time…).
  /^(\/api\/bookings\/)[^/?#]+/,
  // Invitee token — same shape, invitee-scoped.
  /^(\/api\/invitees\/)[^/?#]+/,
  // Time-poll token (/votes, /suggest, /rsvp/confirm, …).
  /^(\/api\/poll\/)[^/?#]+/,
  // Rebook token. Cover both the registered route and the legacy client
  // route so an old cached bundle cannot quietly reopen the leak.
  /^(\/api\/(?:public\/)?rebook\/)[^/?#]+/,
  // GDPR access / erasure confirmation tokens.
  /^(\/api\/gdpr\/(?:access|delete)\/confirm\/)[^/?#]+/,
  // Action-item confirmation: {id} is an ordinary row id, the TRAILING
  // segment is the credential.
  /^(\/api\/public\/action-items\/[^/?#]+\/done\/)[^/?#]+/,
  // Operator email-change confirmation token.
  /^(\/api\/admin\/confirm-email\/)[^/?#]+/,
  // Operator invitation token. The same prefix also serves literal
  // sub-resources (bulk, verify-otp, accept) which must stay readable, and
  // a DELETE by invitation row id — that id is not a secret, but it is
  // indistinguishable from a token at this layer, so it is redacted too.
  // Losing one row id from Sentry is the cheaper mistake.
  /^(\/api\/admin\/invitations\/)(?!(?:bulk|verify-otp|accept)(?:[/?#]|$))[^/?#]+/,
]

// The customer portal and the booking/meeting chat pass the same class of
// access token as a query parameter instead of a path segment.
const TOKEN_QUERY_RULE = /([?&]token=)[^&#]*/gi

// `/videorom` is not an API path at all — it is a PAGE route, and the
// credential rides in its query string:
//   /videorom?url=<whereby participant room URL>&name=…&bookingId=…&token=…
// (built by buildStemneParticipantRoomPath in ./whereby.ts and by
// ManageView.handleJoin). A Whereby room URL is a bearer capability for the
// room: whoever holds it can join the meeting. It is a credential in exactly
// the same sense as the magic token beside it, and TOKEN_QUERY_RULE does not
// cover the key name `url`.
//
// It matters here because Sentry fills `event.request.url` from
// `window.location.href` and puts the same string on `navigation`
// breadcrumbs — so every event captured while an end customer sits in a
// meeting carried both values. #3624 widened the blast radius: portal booking
// cards and the invitee page now land on /videorom instead of navigating
// straight to whereby.com, so far more page loads carry this on a Stemne
// origin.
//
// Scoped to the route rather than applied to every `url=` parameter anywhere:
// the key name is generic, /videorom is the only surface in the app that
// reads `route.query.url`, and blanket-redacting a parameter named `url`
// would cost triage signal on any future return-to link for no gain. Anchored
// like TOKEN_PATH_RULES, so callers must hand this function a path — use
// `redactSentryUrl` below for anything that may carry an origin.
//
// `name=` on the same route is the customer's own full name, put there so the
// Whereby room can label the tile. It is NOT a credential — it is PII, and
// `sendDefaultPii: false` in main.ts says we do not send it. So it is redacted
// too, but with the identifying-data placeholder rather than `[token]`: a
// reader must be able to tell "a secret was here" from "a person was here".
const ROOM_URL_ROUTE = /^\/videorom(?:[/?#]|$)/
const ROOM_URL_QUERY_RULE = /([?&]url=)[^&#]*/gi
const ROOM_NAME_QUERY_RULE = /([?&]name=)[^&#]*/gi

// The two threat models this file answers, named for the models rather than
// for the routes that happen to hit them. `[token]` says a credential was
// here; `[redacted]` says a person was.
const PLACEHOLDER = '[token]'
const IDENTIFYING_PLACEHOLDER = '[redacted]'

/**
 * Replace any credential segment in an API path with `[token]`.
 * Paths that carry no credential are returned unchanged.
 */
export function redactApiPath(path: string): string {
  let out = path
  for (const rule of TOKEN_PATH_RULES) {
    if (rule.test(out)) {
      out = out.replace(rule, `$1${PLACEHOLDER}`)
      break
    }
  }
  out = out.replace(TOKEN_QUERY_RULE, `$1${PLACEHOLDER}`)
  if (ROOM_URL_ROUTE.test(out)) {
    out = out.replace(ROOM_URL_QUERY_RULE, `$1${PLACEHOLDER}`)
    out = out.replace(ROOM_NAME_QUERY_RULE, `$1${IDENTIFYING_PLACEHOLDER}`)
  }
  return out
}

// ---------------------------------------------------------------------------
// Health-adjacent routes — a SEPARATE threat model from the tokens above.
// ---------------------------------------------------------------------------
//
// The rules above protect a SECRET that happens to sit in a path. These rules
// protect a LINKAGE: "customer 3f2a… has a treatment journal", "note entry
// 9c1b… was opened", "the operator searched their notes for <free text>".
// Nothing here is a credential, so `redactApiPath`'s mechanism (anchored
// prefix rules that surgically replace one known segment) doesn't fit — we
// don't know in advance which segment is the id, and on these routes EVERY
// id-shaped segment plus the whole query string is the thing to lose. Hence a
// sibling function with its own list rather than more rules bolted onto
// TOKEN_PATH_RULES: same file because both answer "make this path safe for
// Sentry", two exports because a reader must not mistake one for the other.
//
// Consumer is the `beforeBreadcrumb` hook in main.ts (via redactBreadcrumb.ts)
// — fetch/XHR breadcrumbs carry the raw request URL, which `beforeSend`'s
// key-name scrubbing never looks at.
//
// Matched against the whole URL (breadcrumb URLs are usually absolute:
// `https://api.stemne.no/api/admin/…`), so no `^` anchors here.
//
// Adding a health-adjacent endpoint? Add its shape here.
const HEALTH_ROUTE_RULES: readonly RegExp[] = [
  // Meeting notes: entries, mine, archive, search, comments, mention-grants,
  // share-summary, recipient-view, shared-out, unread-count.
  /\/api\/admin\/notes(?:[/?#]|$)/,
  // Booking-scoped notes, note grants, treatment, journal and meeting wrap.
  /\/api\/admin\/bookings\/[^/?#]+\/(?:notes|note-grants|treatment|journal|wrap)(?:[/?#]|$)/,
  // Treatment vocabulary. No ids in the common shape, but the query string
  // (?category=behandling) still says what kind of practice this org runs.
  /\/api\/admin\/treatment-types(?:[/?#]|$)/,
  /\/api\/admin\/tiltak-options(?:[/?#]|$)/,
  // Customer-scoped: personal notes, the by-email notes list, and the
  // per-customer NOTES search. Deliberately not a bare `search` match —
  // /api/admin/customers/search (the customer directory) and the other
  // /search endpoints are not health data and must pass through untouched.
  /\/api\/admin\/customers\/[^/?#]+\/(?:personal-notes|notes|search)(?:[/?#]|$)/,
  /\/api\/admin\/customers\/[^/?#]+\/prep\/note(?:[/?#]|$)/,
]

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Identifying = it points at one person or one record. Shape-based rather
// than a literal allow-list so a new sub-resource (`/summary`, `/export`)
// keeps reading as itself instead of turning into noise.
function isIdentifyingSegment(segment: string): boolean {
  if (!segment) return false
  if (UUID_SEGMENT.test(segment)) return true
  // Numeric row id.
  if (/^\d+$/.test(segment)) return true
  // Customer email as a path segment, raw or percent-encoded.
  if (segment.includes('@') || /%40/i.test(segment)) return true
  return false
}

/**
 * Strip the identifying parts out of a health-adjacent API URL while keeping
 * its shape, so a Sentry breadcrumb still says "this was a notes-entries
 * fetch" without saying whose.
 *
 * URLs on any other route are returned byte-for-byte unchanged — no
 * speculative redaction, because over-redacting everything would cost the
 * triage signal the breadcrumbs exist for.
 *
 * The query string is dropped WHOLESALE on a match: `?q=` on
 * /api/admin/notes/search is free text the operator typed about a patient,
 * which is more sensitive than any id in the path.
 */
export function redactHealthApiPath(url: string): string {
  if (!HEALTH_ROUTE_RULES.some((rule) => rule.test(url))) return url

  const queryAt = url.search(/[?#]/)
  const path = queryAt === -1 ? url : url.slice(0, queryAt)
  const query = queryAt === -1 ? '' : `${url[queryAt]}${IDENTIFYING_PLACEHOLDER}`

  const scrubbed = path
    .split('/')
    .map((segment) => (isIdentifyingSegment(segment) ? IDENTIFYING_PLACEHOLDER : segment))
    .join('/')

  return scrubbed + query
}

// ---------------------------------------------------------------------------
// The composition both Sentry hooks use.
// ---------------------------------------------------------------------------
//
// `redactApiPath`'s rules are anchored to the start of the string and expect a
// bare path, but the strings Sentry hands us are frequently absolute: a
// breadcrumb URL for the cross-origin API host, and `event.request.url`, which
// the SDK fills from `window.location.href` and is therefore ALWAYS absolute.
// Strip the origin before either redactor runs and put it back after, so the
// anchors still line up.
//
// Lives here rather than in a hook module because there are now two callers —
// `redactHealthBreadcrumb` (fetch/xhr/navigation crumbs) and
// `redactSentryRequest` (the event's own request URL) — and a second copy of
// this composition is exactly how one of them would quietly fall behind the
// other's rule set.

const ORIGIN = /^[a-z][a-z0-9+.-]*:\/\/[^/]+/i

/**
 * Make a URL — absolute or relative — safe to ship to Sentry: credential path
 * segments and query parameters become `[token]`, health-adjacent routes lose
 * their identifying parts, everything else is returned byte-for-byte.
 */
export function redactSentryUrl(url: string): string {
  const match = url.match(ORIGIN)
  const origin = match ? match[0] : ''
  const rest = match ? url.slice(match[0].length) : url
  // Order doesn't matter: the two rule sets target disjoint route shapes
  // (customer-facing token paths vs. admin health routes), so neither can
  // observe the other's placeholder.
  return origin + redactApiPath(redactHealthApiPath(rest))
}
